/**
 * Hook for fetching JSONCargo live tracking data for a container.
 *
 * Uses GET /tracking/containers/{id}/latest-tracking (cached, no extra API cost).
 * Returns raw JSONCargo response with all tracking fields.
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface JSONCargoTrackingData {
  container_id: string;
  container_type: string | null;
  container_status: string | null;
  shipping_line_name: string | null;
  shipping_line_id: string | null;
  tare: string | null;
  shipped_from: string | null;
  shipped_from_terminal: string | null;
  shipped_to: string | null;
  shipped_to_terminal: string | null;
  atd_origin: string | null;
  eta_final_destination: string | null;
  last_location: string | null;
  last_location_terminal: string | null;
  next_location: string | null;
  next_location_terminal: string | null;
  atd_last_location: string | null;
  eta_next_destination: string | null;
  timestamp_of_last_location: string | null;
  last_movement_timestamp: string | null;
  loading_port: string | null;
  discharging_port: string | null;
  last_vessel_name: string | null;
  last_voyage_number: string | null;
  current_vessel_name: string | null;
  current_voyage_number: string | null;
  last_updated: string | null;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const TRACKING_QUERY_KEYS = {
  latestTracking: (containerId: string) =>
    ["tracking", "latest", containerId] as const,
};

// ============================================================================
// STATUS MAPPING (display-only, mirrors backend logic)
// ============================================================================

export type PhaseId = "draft" | "booked" | "in_transit" | "arrived" | "delivered";

export interface PhaseSubStatus {
  phaseId: PhaseId;
  label: string;
  isWarning: boolean;
}

const STATUS_TO_PHASE: [string, PhaseId, string, boolean][] = [
  // [prefix, phaseId, label, isWarning]
  // Order: longest prefix first (for correct matching)
  ["gate out for delivery", "delivered", "Out for delivery", false],
  ["out for delivery", "delivered", "Out for delivery", false],
  ["empty returned", "delivered", "Empty returned", false],
  ["empty return", "delivered", "Empty returned", false],
  ["available for pickup", "arrived", "Available for pickup", false],
  ["customs hold", "arrived", "Customs hold", true],
  ["document hold", "arrived", "Document hold", true],
  ["line hold", "arrived", "Line hold", true],
  ["inspection", "arrived", "Under inspection", true],
  ["gate out empty", "booked", "Empty container picked up", false],
  ["load on vessel", "in_transit", "Loaded on vessel", false],
  ["vessel departure", "in_transit", "Vessel departed", false],
  ["vessel departed", "in_transit", "Vessel departed", false],
  ["vessel arrival", "arrived", "Vessel arrived", false],
  ["vessel arrived", "arrived", "Vessel arrived", false],
  ["transshipment", "in_transit", "Transshipment", false],
  ["discharged", "arrived", "Discharged from vessel", false],
  ["in transit", "in_transit", "In transit", false],
  ["in-transit", "in_transit", "In transit", false],
  ["delivered", "delivered", "Delivered", false],
  ["departed", "in_transit", "Vessel departed", false],
  ["gate out", "arrived", "Gate out from terminal", false],
  ["gate-out", "arrived", "Gate out from terminal", false],
  ["gate in", "booked", "Gate in at terminal", false],
  ["gate-in", "booked", "Gate in at terminal", false],
  ["loaded", "in_transit", "Loaded on vessel", false],
  ["loading", "booked", "Loading", false],
  ["arrived", "arrived", "Vessel arrived", false],
  ["booked", "booked", "Booking confirmed", false],
];

/**
 * Parse JSONCargo container_status string into a display-only phase sub-status.
 * Uses prefix matching (same logic as backend).
 */
export function getPhaseSubStatus(containerStatus: string | null | undefined): PhaseSubStatus | null {
  if (!containerStatus) return null;
  const lower = containerStatus.toLowerCase();
  for (const [prefix, phaseId, label, isWarning] of STATUS_TO_PHASE) {
    if (lower.startsWith(prefix)) {
      return { phaseId, label, isWarning };
    }
  }
  return null;
}

// ============================================================================
// HOOK
// ============================================================================

export function useContainerLatestTracking(
  containerId: string | undefined,
  containerNumber: string | undefined | null,
) {
  return useQuery({
    queryKey: TRACKING_QUERY_KEYS.latestTracking(containerId!),
    queryFn: () =>
      apiGet<JSONCargoTrackingData>(
        `/tracking/containers/${containerId}/latest-tracking`,
      ),
    enabled: !!containerId && !!containerNumber,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
